<template>
	<div class="missions-container">
		<Header :title="'Missions'" :user="currentUser" />

		<main class="page-main">
			<div style="display:flex;gap:1rem;margin-bottom:2rem;justify-content:flex-end;">
				<button @click="createNewMission" class="action-btn construct-btn">New Mission</button>
			</div>
			<div v-if="loading" class="loading">Loading missions...</div>
			<div v-else-if="missions.length === 0" class="empty-state">
				<p>No missions found</p>
			</div>
			<div v-else class="tiles-container">
				<div
					v-for="mission in missions"
					:key="mission.id"
					class="tile"
					@click="editMission(mission.id)"
				>
					<div class="mission-info">
						<h3>{{ mission.name }}</h3>
						<p class="mission-time">Total Time: {{ formatTime(mission.totalTime) }}</p>
						<p class="mission-steps">Steps: {{ Array.isArray(mission.steps) ? mission.steps.length : 0 }}</p>
						<p v-if="typeof mission.user_id === 'string' && getChildName(mission.user_id)">Assigned to: {{ getChildName(mission.user_id) }}</p>
					</div>
					<div class="action-btn-bar">
						<button @click.stop="runMission(mission.id)" class="action-btn construct-btn">Run</button>
						<button @click.stop="deleteMission(mission.id)" class="action-btn destruct-btn">Delete</button>
					</div>
				</div>
			</div>
		</main>
	</div>
</template>

<script setup lang="ts">
const familyChildren = ref<User[]>([])
const getChildName = (id: string) => {
	const child = familyChildren.value.find(u => u.id === id && u.type === 'child');
	return child ? child.name : '';
}
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import Header from '../components/Header.vue'
import { dbService, type Mission, type User } from '../services/database'

const currentUser = ref<User | null>(null)
onMounted(async () => {
	const userStr = sessionStorage.getItem('currentUser')
	if (userStr) currentUser.value = JSON.parse(userStr)
	// Load family children for lookup
	if (currentUser.value && currentUser.value.id) {
		const all = await dbService.getUsers()
		familyChildren.value = all.filter((u: any) => u.account_id === currentUser.value?.id && u.type === 'child')
	}
})
const router = useRouter()
const missions = ref<Mission[]>([])
const loading = ref(false)

const formatTime = (seconds: number): string => {
	if (typeof seconds !== 'number' || isNaN(seconds) || seconds < 0) {
		return '0m 0s';
	}
	const minutes = Math.floor(seconds / 60);
	const secs = seconds % 60;
	return `${minutes}m ${secs}s`;
}

const goBack = () => {
	router.push('/dashboard')
}

const goDashboard = () => {
	router.push('/dashboard')
}

const createNewMission = () => {
	router.push('/edit-mission')
}

const editMission = (id: string) => {
	router.push(`/edit-mission/${id}`)
}

const runMission = (id: string) => {
	router.push(`/mission-runner/${id}`)
}

const deleteMission = async (id: string) => {
	if (confirm('Are you sure you want to delete this mission?')) {
		try {
			await dbService.deleteMission(id)
			await loadMissions()
		} catch (error) {
			alert('Failed to delete mission: ' + (error instanceof Error ? error.message : 'Unknown error'))
		}
	}
}

const loadMissions = async () => {
	loading.value = true
	try {
		const rawMissions = await dbService.getMissions();
		missions.value = rawMissions.map((mission: any) => {
			let totalTime = 0;
			let steps: any[] = [];
			if (mission.data) {
				try {
					const parsed = typeof mission.data === 'string' ? JSON.parse(mission.data) : mission.data;
					totalTime = typeof parsed.totalTime === 'number' ? parsed.totalTime : 0;
					steps = Array.isArray(parsed.steps) ? parsed.steps : [];
				} catch (e) {
					// ignore parse errors
				}
			}
			return {
				...mission,
				totalTime,
				steps
			};
		});
	} catch (error) {
		alert('Failed to load missions: ' + (error instanceof Error ? error.message : 'Unknown error'));
	} finally {
		loading.value = false;
	}
}

onMounted(loadMissions)
</script>

<style scoped>
.missions-container {
	min-height: 100vh;
	background-color: #f5f5f5;
}
.page-header {
	background: white;
	padding: 1rem 2rem;
	border-bottom: 1px solid #ddd;
	display: flex;
	justify-content: space-between;
	align-items: center;
}
.page-header h1 {
	margin: 0;
	color: #333;
}
.page-main {
	padding: 2rem;
}
.tiles-container {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
	gap: 2rem;
}
.tile {
	background: white;
	border-radius: 8px;
	box-shadow: 0 2px 8px rgba(0,0,0,0.04);
	padding: 2rem;
	cursor: pointer;
	transition: box-shadow 0.2s;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	gap: 1rem;
}
.tile:hover {
	box-shadow: 0 4px 16px rgba(0,0,0,0.08);
}
.mission-info h3 {
	margin: 0 0 0.5rem 0;
	font-size: 1.3rem;
	color: #222;
}
.mission-time, .mission-steps {
	font-size: 1rem;
	color: #666;
	margin: 0.25rem 0;
}
.mission-actions {
	display: flex;
	gap: 1rem;
	justify-content: flex-end;
}
.action-btn {
	padding: 0.5rem 1.2rem;
	border: none;
	border-radius: 4px;
	background-color: #666;
	color: white;
	cursor: pointer;
	font-size: 1rem;
	font-weight: 500;
}
.action-btn:hover {
	background-color: #555;
}
.construct-btn {
	background-color: #2d8f2d;
}
.construct-btn:hover {
	background-color: #267a26;
}
.destruct-btn {
	background-color: #c0392b;
}
.destruct-btn:hover {
	background-color: #a93226;
}
.loading {
	text-align: center;
	color: #888;
	font-size: 1.2rem;
	margin-top: 2rem;
}
.empty-state {
	text-align: center;
	color: #888;
	font-size: 1.2rem;
	margin-top: 2rem;
}
</style>